import apiClient from '@/shared/services/apiClient'
import type {
  ApiResponse,
  TestSeriesResponse,
  PagedAssessmentResponse,
  AssessmentResponse,
  CompleteAssessmentResponse,
  TestSeriesSearchRequest,
  AssessmentSearchRequest,
} from '../types'

class TestSeriesService {
  private readonly baseUrl = '/api/v1/test-series'

  /**
   * Get all test series (non-paginated)
   */
  async getAllTestSeries(): Promise<ApiResponse<TestSeriesResponse[]>> {
    const response = await apiClient.get<ApiResponse<TestSeriesResponse[]>>(this.baseUrl)
    return response.data
  }

  /**
   * Get test series by code
   * @param seriesCode - Test series code (e.g., "NEET_2026_LAUNCH")
   */
  async getTestSeriesByCode(seriesCode: string): Promise<ApiResponse<TestSeriesResponse>> {
    const response = await apiClient.get<ApiResponse<TestSeriesResponse>>(
      `${this.baseUrl}/${seriesCode}`
    )
    return response.data
  }

  /**
   * Get assessments in a test series
   * @param seriesCode - Test series code
   * @param page - Page number (0-indexed)
   * @param size - Number of items per page
   * @param sort - Sort criteria (e.g., "displayOrder,asc")
   */
  async getAssessmentsInSeries(
    seriesCode: string,
    page = 0,
    size = 20,
    sort = 'displayOrder,asc'
  ): Promise<PagedAssessmentResponse> {
    const response = await apiClient.get<PagedAssessmentResponse>(
      `${this.baseUrl}/${seriesCode}/assessments`,
      {
        params: { page, size, sort },
      }
    )
    return response.data
  }

  /**
   * Search and filter test series
   * @param filters - Search criteria
   * @param page - Page number (0-indexed)
   * @param size - Number of items per page
   * @param sort - Sort criteria
   */
  async searchTestSeries(
    filters?: TestSeriesSearchRequest,
    page = 0,
    size = 20,
    sort = 'createdAt,desc'
  ): Promise<any> {
    const response = await apiClient.post(
      `${this.baseUrl}/search`,
      filters,
      {
        params: { page, size, sort },
      }
    )
    return response.data
  }
}

// Assessment Service
class AssessmentService {
  private readonly baseUrl = '/api/v1/assessments'

  /**
   * Get assessment by code
   * @param testCode - Assessment code (e.g., "NEET_2025_FLT_001")
   */
  async getAssessmentByCode(testCode: string): Promise<ApiResponse<AssessmentResponse>> {
    const response = await apiClient.get<ApiResponse<AssessmentResponse>>(
      `${this.baseUrl}/${testCode}`
    )
    return response.data
  }

  /**
   * Search and filter assessments
   * @param filters - Search criteria
   * @param page - Page number (0-indexed)
   * @param size - Number of items per page
   * @param sort - Sort criteria
   */
  async searchAssessments(
    filters?: AssessmentSearchRequest,
    page = 0,
    size = 20,
    sort = 'createdAt,desc'
  ): Promise<PagedAssessmentResponse> {
    const response = await apiClient.post<PagedAssessmentResponse>(
      `${this.baseUrl}/search`,
      filters,
      {
        params: { page, size, sort },
      }
    )
    return response.data
  }

  /**
   * Get complete assessment with sections and questions
   * @param testCode - Assessment code (e.g., "NEET_2025_FLT_001")
   */
  async getCompleteAssessment(testCode: string): Promise<ApiResponse<CompleteAssessmentResponse>> {
    const response = await apiClient.get<ApiResponse<CompleteAssessmentResponse>>(
      `${this.baseUrl}/${testCode}/complete`
    )
    return response.data
  }
}

export const testSeriesService = new TestSeriesService()
export const assessmentService = new AssessmentService()
